/**
 * Copyright 2025 Amazon.com, Inc. or its affiliates. All Rights Reserved.
 * SPDX-License-Identifier: MIT-0
 */

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { signIn, signOut, getCurrentUser, fetchAuthSession, confirmSignIn } from 'aws-amplify/auth';

export type AdminRole = 'SuperAdmin' | 'Operations' | 'CustomerService' | 'Finance' | 'TechSupport';

export const ADMIN_ROLES: AdminRole[] = ['SuperAdmin', 'Operations', 'CustomerService', 'Finance', 'TechSupport'];

export interface AuthUser {
  username: string;
  userId: string;
  attributes?: Record<string, string>;
  groups?: string[];
}

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: AuthUser | null;
  isAdmin: boolean;
  roles: AdminRole[];
  hasRole: (...roles: AdminRole[]) => boolean;
  login: (username: string, password: string) => Promise<{ success: boolean; needsConfirmation?: boolean; nextStep?: any; error?: string }>;
  confirmLogin: (challengeResponse: string) => Promise<{ success: boolean; nextStep?: any; error?: string }>;
  checkAuthStatus: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [roles, setRoles] = useState<AdminRole[]>([]);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const currentUser = await getCurrentUser();
      const session = await fetchAuthSession();

      if (currentUser && session.tokens) {
        const groups = session.tokens.idToken?.payload['cognito:groups'] as string[] | undefined ?? [];
        const email = session.tokens.idToken?.payload['email'] as string | undefined
          ?? session.tokens.idToken?.payload['cognito:username'] as string | undefined
          ?? currentUser.username;
        const name = (session.tokens.idToken?.payload['name'] as string | undefined)?.trim();
        const userRoles = ADMIN_ROLES.filter(r => groups.includes(r));
        const hasAdminAccess = userRoles.length > 0;

        setUser({ username: name || email, userId: currentUser.userId, groups });
        setIsAdmin(hasAdminAccess);
        setRoles(userRoles);
        setIsAuthenticated(true);
      } else {
        resetAuth();
      }
    } catch {
      resetAuth();
    } finally {
      setIsLoading(false);
    }
  };

  const resetAuth = () => {
    setIsAuthenticated(false);
    setIsAdmin(false);
    setRoles([]);
    setUser(null);
  };

  const login = async (username: string, password: string) => {
    try {
      // Clear any stale Amplify session state before signing in
      try { await signOut(); } catch { /* ignore — may not be signed in */ }
      const result = await signIn({ username, password, options: { authFlowType: 'USER_PASSWORD_AUTH' } });
      if (result.isSignedIn) {
        await checkAuthStatus();
        return { success: true };
      }
      if (result.nextStep) {
        return { success: false, needsConfirmation: true, nextStep: result.nextStep };
      }
      return { success: false, error: 'Sign in incomplete' };
    } catch (error: any) {
      return { success: false, error: error.message || 'Login failed' };
    }
  };

  const confirmLogin = async (challengeResponse: string) => {
    try {
      const result = await confirmSignIn({ challengeResponse });
      if (result.isSignedIn) {
        await checkAuthStatus();
        return { success: true };
      }
      if (result.nextStep) {
        return { success: false, nextStep: result.nextStep };
      }
      return { success: false, error: 'Confirmation failed' };
    } catch (error: any) {
      return { success: false, error: error.message || 'Confirmation failed' };
    }
  };

  const logout = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      resetAuth();
    }
  };

  const hasRole = (...checkRoles: AdminRole[]) =>
    checkRoles.some(r => roles.includes(r));

  return (
    <AuthContext.Provider value={{ isAuthenticated, isLoading, user, isAdmin, roles, hasRole, login, confirmLogin, checkAuthStatus, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) throw new Error('useAuth must be used within an AuthProvider');
  return context;
}
